<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ChatComposerAsk from '../ChatComposerAsk/ChatComposerAsk.vue'
import { useChatStore } from '@/stores/chat'
import { useContentStore } from '@/stores/content'
import { useStageStore } from '@/stores/stage'
import { useChatThreadSwitcher } from '@/composables/useChatThreadSwitcher'
import { useChatRoute } from '@/composables/useChatRoute'
import { useComposerPlaceholders } from '@/composables/useComposerPlaceholders'
import type { PendingImageHandoff } from '@/composables/usePromptImageAttachments'
import { isLoaded } from '@/types/load-state'
import type { ContextItem } from '../AppChatPanel/context-types'

withDefaults(
  defineProps<{
    /** The empty-chat welcome: the composer alone, wrapped in the halo and offering rotating example prompts. */
    welcome?: boolean
  }>(),
  { welcome: false },
)

const chat = useChatStore()
const stage = useStageStore()
const content = useContentStore()
const { withThreadAddress } = useChatRoute()
const { closedThreadMessage } = useChatThreadSwitcher()
const composerPlaceholders = useComposerPlaceholders()

const value = ref('')
const questionTyping = ref(false)

const isStreamingTurn = computed(
  () => chat.sendStatus === 'submitting' || chat.sendStatus === 'streaming',
)

const composerDisabledMessage = computed(() =>
  chat.isActiveThreadClosed ? closedThreadMessage : undefined,
)

const openAction = computed(() =>
  chat.visibleAction?.status === 'pending' ? chat.visibleAction : undefined,
)

const openQuestion = computed(() =>
  chat.visibleQuestion?.status === 'pending' ? chat.visibleQuestion : undefined,
)

const contextItems = computed<ContextItem[]>(() => [
  ...(chat.contextScreen
    ? [{ id: 'screen', label: chat.contextScreen, kind: 'section' as const, locked: true }]
    : []),
  ...(chat.attachment
    ? [
        {
          id: 'attachment',
          label: chat.attachment.chip ?? chat.attachment.ref,
          kind: chat.attachment.kind,
          ...(!chat.attachment.chip && chat.attachment.excerpt?.trim()
            ? { excerpt: chat.attachment.excerpt.trim() }
            : {}),
        },
      ]
    : []),
])

function removeContext(id: string): void {
  if (id === 'attachment') chat.clearAttachment()
}

function resolvePostBody(postId: string): string | undefined {
  if (!isLoaded(content.posts)) return undefined
  return content.posts.value.find((post) => post.id === postId)?.copy
}

watch(
  () => chat.visibleQuestion?.id,
  () => {
    questionTyping.value = false
  },
)

watch(
  () => chat.draft,
  (draft) => {
    if (draft === null) return
    value.value = chat.takeDraft() ?? ''
  },
  { immediate: true },
)

watch(value, (text) => {
  if (isStreamingTurn.value) stage.setUserBusy(text.length > 0)
})

function handleAnswerQuestion(answer: string): void {
  const question = openQuestion.value
  if (!question) return
  void withThreadAddress(() => chat.answerQuestion(question.id, answer))
}

function handleAnswerQuestionSubmit(text: string): void {
  value.value = ''
  questionTyping.value = false
  handleAnswerQuestion(text)
}

function handleSkipConnect(): void {
  if (chat.visibleConnect) chat.dismissConnect(chat.visibleConnect.id)
}

function handleSubmit(text: string, attachments: PendingImageHandoff[] = []): void {
  value.value = ''
  stage.setUserBusy(false)
  void withThreadAddress(() => chat.send(text, {}, attachments))
}

function handleRetry(): void {
  stage.setUserBusy(false)
  void withThreadAddress(() => chat.retry())
}
</script>

<template>
  <ChatComposerAsk
    v-model="value"
    :open-action="openAction"
    :resolve-body="resolvePostBody"
    :open-question="openQuestion"
    :question-typing="questionTyping"
    :connect="chat.visibleConnect"
    :pending="isStreamingTurn"
    :status="chat.sendStatus"
    :retryable="chat.canRetry"
    :disabled="chat.isActiveThreadClosed"
    :disabled-message="composerDisabledMessage"
    :context-items="contextItems"
    :halo="welcome"
    :placeholders="welcome ? composerPlaceholders : undefined"
    @confirm="chat.confirmAction"
    @decline="chat.declineAction"
    @answer="handleAnswerQuestion"
    @type-answer="questionTyping = true"
    @answer-submit="handleAnswerQuestionSubmit"
    @skip-connect="handleSkipConnect"
    @remove-context="removeContext"
    @submit="handleSubmit"
    @stop="chat.stop"
    @retry="handleRetry"
  />
</template>
